import {useId} from 'react';
import {type Control, type UseFormRegister, useWatch} from 'react-hook-form';

import ChevronDownIcon from '@/components/Icons/ChevronDownIcon';
import {twx} from '@/utils/tailwindUtils';

type AgeGateFormValues = {
    month: string;
    year: string;
};

interface DateInputOption {
    value: string;
    label: string;
}

interface DateInputProps {
    name: keyof AgeGateFormValues;
    title?: string;
    options: DateInputOption[];
    register: UseFormRegister<AgeGateFormValues>;
    control: Control<AgeGateFormValues>;
    ariaLabel?: string;
    placeholder?: string;
    isLoading: boolean;
    testId?: string;
}

const DateInput = (props: DateInputProps) => {
    const {name, title, options, register, control, ariaLabel, placeholder, isLoading, testId} = props;
    const value = useWatch({control, name});
    const inputId = useId();

    const registerProps = register(name, {
        required: true,
    });

    return (
        <div className='flex flex-1 flex-col'>
            {title && (
                <label
                    htmlFor={inputId}
                    className='mb-1 text-start text-12 leading-[16px] tracking-0 text-primary-dark-85'
                >
                    {title}
                </label>
            )}
            <div className='relative'>
                <select
                    id={inputId}
                    disabled={isLoading}
                    data-testid={testId}
                    className={twx(
                        'w-full appearance-none rounded bg-transparent px-4 py-4 pr-10 text-16 leading-15 outline-none',
                        value
                            ? 'border-2 border-primary-dark text-primary-dark'
                            : 'border border-primary-dark-85 text-primary-dark-85',
                        'focus:border-primary-dark focus:text-primary-dark'
                    )}
                    aria-label={ariaLabel}
                    {...registerProps}
                >
                    <option value='' disabled hidden>
                        {placeholder}
                    </option>
                    {options.map((opt) => (
                        <option className='text-primary-dark' key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                <ChevronDownIcon className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2' />
            </div>
        </div>
    );
};

export default DateInput;
